/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState, useEffect } from 'react';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  LabelList
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Layers, 
  FileText, 
  Clock, 
  AlertTriangle, 
  Truck, 
  DollarSign,
  Award,
  ShieldCheck
} from 'lucide-react';
import { Folio, PreInvoice } from '../types.ts';
import { CertificatesExpirationBanner } from './CertificatesExpirationBanner.tsx';
import { 
  loadAllCertificatesFromStorage, 
  evaluateCertificatesAlerts, 
  CertificatesAlertEvaluation 
} from '../utils/certificateAlertHelper.ts';

interface DashboardStatsProps {
  folios: Folio[];
  preInvoices: PreInvoice[];
  exchangeRate: number;
  certAlertEvaluation?: CertificatesAlertEvaluation;
  onNavigateToCertificates?: () => void;
}

const COLORS = ['#bc1100', '#2e302e', '#787878', '#52554e', '#d90209', '#989898', '#10b981', '#f59e0b'];

export default function DashboardStats({ 
  folios, 
  preInvoices, 
  exchangeRate,
  certAlertEvaluation: initialCertEvaluation,
  onNavigateToCertificates
}: DashboardStatsProps) {
  
  // Real-time certification alert evaluation on dashboard mount and storage changes
  const [certAlertEval, setCertAlertEval] = useState<CertificatesAlertEvaluation>(() => {
    if (initialCertEvaluation) return initialCertEvaluation;
    const certs = loadAllCertificatesFromStorage();
    return evaluateCertificatesAlerts(certs);
  });

  useEffect(() => {
    if (initialCertEvaluation) {
      setCertAlertEval(initialCertEvaluation);
    }
  }, [initialCertEvaluation]);

  useEffect(() => {
    const refreshCerts = () => {
      const certs = loadAllCertificatesFromStorage();
      setCertAlertEval(evaluateCertificatesAlerts(certs));
    };

    window.addEventListener('snf_certificates_updated', refreshCerts);
    window.addEventListener('storage', refreshCerts);

    // Initial check refresh
    refreshCerts();

    return () => {
      window.removeEventListener('snf_certificates_updated', refreshCerts);
      window.removeEventListener('storage', refreshCerts);
    };
  }, []);
  
  // Calculate general KPI statistics
  const kpis = useMemo(() => {
    const totalFolios = folios.length;
    
    const activeFolios = folios.filter(f => f.status !== 'ENTREGA_PLANTA' && f.status !== 'DEVOLUCION').length;
    
    const inDecapado = folios.filter(f => f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO').length;
    
    const totalRework = folios.filter(f => f.status === 'RETRABAJO').length;
    
    // Helper to calculate folio total amount in sync with BillingManager (Mis Facturas / Cobros)
    const getFolioAmount = (f: Folio) => {
      let sumItems = 0;
      if (f.items && f.items.length > 0) {
        sumItems = f.items.reduce((s, item) => {
          const totalVal = Number(item.totalMXN);
          if (!isNaN(totalVal) && totalVal > 0) return s + totalVal;
          const unitP = Number(item.unitPriceMXN);
          const qty = Number(item.quantity) || 1;
          if (!isNaN(unitP) && unitP > 0) return s + (unitP * qty);
          return s;
        }, 0);
      }
      if (sumItems > 0) return sumItems;

      const matchingInv = preInvoices.find(inv => inv.folioId === f.id);
      if (matchingInv && Number(matchingInv.totalMXN) > 0) {
        return Number(matchingInv.totalMXN);
      }

      return 25000;
    };

    // Compute GR CONFIRMADO total strictly in sync with BillingManager (totalGrConfirmado)
    const confirmedFolios = folios.filter(f => f.status === 'ENTREGA_PLANTA' && f.grStatus === 'CONFIRMADO');
    const totalConfirmedMXN = confirmedFolios.reduce((sum, f) => sum + getFolioAmount(f), 0);

    const totalMXN = totalConfirmedMXN;
    const fx = exchangeRate || 19.5;
    const totalUSD = Math.round((totalMXN / fx) * 100) / 100;

    // Calculate Rework rate
    const totalInspected = folios.filter(f => f.history.some(h => h.stage === 'INSPECCION')).length;
    const reworkHistoryCount = folios.filter(f => f.history.some(h => h.stage === 'RETRABAJO')).length;
    const reworkRate = totalInspected > 0 ? Math.round((reworkHistoryCount / totalInspected) * 100) : 0;

    return {
      totalFolios,
      activeFolios,
      inDecapado,
      totalRework,
      totalMXN,
      totalUSD,
      reworkRate
    };
  }, [folios, preInvoices, exchangeRate]);

  // Chart 1: Folios by Process Stage
  const stageChartData = useMemo(() => {
    const counts: Record<string, number> = {
      'RECOLECCION': 0,
      'ENTRADA_CEDIS': 0,
      'DECAPADO': 0, // Combining thermic and wash
      'INSPECCION': 0,
      'RETRABAJO': 0,
      'SALIDA_CEDIS': 0,
      'ENTREGA_PLANTA': 0,
    };

    folios.forEach(f => {
      if (f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO') {
        counts['DECAPADO']++;
      } else if (f.status === 'DEVOLUCION') {
        counts['ENTREGA_PLANTA']++; // show inside final state
      } else if (counts[f.status] !== undefined) {
        counts[f.status]++;
      }
    });

    return Object.entries(counts).map(([name, val]) => ({
      name: name.replace('_', ' '),
      cantidad: val
    }));
  }, [folios]);

  // Chart 2: Client logistics volume
  const clientChartData = useMemo(() => {
    const clients: Record<string, number> = {};
    folios.forEach(f => {
      clients[f.clientName] = (clients[f.clientName] || 0) + 1;
    });

    return Object.entries(clients).map(([name, value]) => ({
      name: name.length > 18 ? name.substring(0, 15) + '...' : name,
      value
    }));
  }, [folios]);

  // Chart 3: Processing Durations simulation based on complete history
  const durationChartData = useMemo(() => {
    // Collect average duration per completed stage from history in minutes
    const stageDurations: Record<string, { sum: number, count: number }> = {};
    
    folios.forEach(f => {
      f.history.forEach((h, idx) => {
        let min = 0;
        if (h.durationMs && h.durationMs > 0) {
          min = h.durationMs / 60000;
        } else if (f.history[idx + 1] && f.history[idx + 1].timestamp && h.timestamp) {
          const diff = new Date(f.history[idx + 1].timestamp).getTime() - new Date(h.timestamp).getTime();
          if (diff > 0) {
            min = diff / 60000;
          }
        }

        if (min > 0) {
          if (!stageDurations[h.stage]) {
            stageDurations[h.stage] = { sum: 0, count: 0 };
          }
          stageDurations[h.stage].sum += min;
          stageDurations[h.stage].count += 1;
        }
      });
    });

    const STAGE_ORDER: { key: string; label: string; shortLabel: string; icon: string }[] = [
      { key: 'RECOLECCION', label: '1. Recolección', shortLabel: 'RECOLECCIÓN', icon: '🚚' },
      { key: 'ENTRADA_CEDIS', label: '2. Entrada CEDIS', shortLabel: 'ENTRADA CEDIS', icon: '📥' },
      { key: 'DECAPADO_TERMICO_QUIMICO', label: '3.1 Decapado T/Q', shortLabel: 'DECAPADO T/Q', icon: '🔥' },
      { key: 'DECAPADO_LAVADO', label: '3.2 Lavado Neutro', shortLabel: 'LAVADO', icon: '💧' },
      { key: 'INSPECCION', label: '4. Inspección CC', shortLabel: 'INSPECCIÓN CC', icon: '🔍' },
      { key: 'SALIDA_CEDIS', label: '5. Salida CEDIS', shortLabel: 'SALIDA CEDIS', icon: '📦' },
      { key: 'ENTREGA_PLANTA', label: '6. Entrega Planta', shortLabel: 'ENTREGA PLANTA', icon: '🏭' },
    ];
    
    return STAGE_ORDER.map(cat => {
      const entry = stageDurations[cat.key];
      const avg = entry && entry.count > 0 ? Math.round(entry.sum / entry.count) : 0;
      const hours = (avg / 60).toFixed(1);
      return {
        key: cat.key,
        etapa: cat.shortLabel,
        fullLabel: cat.label,
        icon: cat.icon,
        'Minutos Promedio': avg,
        minutes: avg,
        displayLabel: avg >= 60 ? `${avg.toLocaleString()}m (${hours}h)` : `${avg}m`,
        fullFormatted: avg >= 60 ? `${avg.toLocaleString()} min (~${hours} hrs)` : `${avg} min`,
        sampleCount: entry ? entry.count : 0
      };
    });
  }, [folios]);

  return (
    <div className="space-y-4">
      
      {/* Real-time Visual Alert Banner for Certifications Expiring in < 30 Days */}
      {certAlertEval.hasUrgentAlerts && (
        <CertificatesExpirationBanner
          evaluation={certAlertEval}
          onNavigateToCertificates={onNavigateToCertificates}
        />
      )}

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5" id="dashboard-kpi-grid">
        
        {/* KPI 1 */}
        <div className="bg-white p-3.5 rounded-lg border border-slate-200/80 shadow-tiny hover:border-slate-300 transition-all flex items-center justify-between" id="kpi-active-folios">
          <div className="space-y-1">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider font-sans">Folios Activos</p>
            <h3 className="text-2xl font-display font-extrabold text-slate-900 tracking-tight leading-none">{kpis.activeFolios}</h3>
            <span className="text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-sm font-semibold inline-block border border-blue-100">En curso</span>
          </div>
          <div className="p-2 bg-blue-50 rounded-md text-blue-600 border border-blue-100/50">
            <Layers className="h-5 w-5" />
          </div>
        </div>

        {/* KPI 2 */}
        <div className="bg-white p-3.5 rounded-lg border border-slate-200/80 shadow-tiny hover:border-slate-300 transition-all flex items-center justify-between" id="kpi-decapado">
          <div className="space-y-1">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider font-sans">En Decapado</p>
            <h3 className="text-2xl font-display font-extrabold text-amber-600 tracking-tight leading-none">{kpis.inDecapado}</h3>
            <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm font-semibold inline-block border border-amber-100">T/Q + Lavado</span>
          </div>
          <div className="p-2 bg-amber-50 rounded-md text-amber-600 border border-amber-100/50">
            <Clock className="h-5 w-5" />
          </div>
        </div>

        {/* KPI 3 */}
        <div className="bg-white p-3.5 rounded-lg border border-slate-200/80 shadow-tiny hover:border-slate-300 transition-all flex items-center justify-between" id="kpi-rework">
          <div className="space-y-1">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider font-sans">Tasa de Retrabajo</p>
            <h3 className="text-2xl font-display font-extrabold text-rose-600 tracking-tight leading-none">{kpis.reworkRate}%</h3>
            <span className="text-[10px] text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded-sm font-semibold inline-block border border-rose-100">
              {kpis.totalRework} en reproceso
            </span>
          </div>
          <div className="p-2 bg-rose-50 rounded-md text-rose-500 border border-rose-100/50">
            <AlertTriangle className="h-5 w-5" />
          </div>
        </div>

        {/* KPI 4 */}
        <div className="bg-white p-3.5 rounded-lg border border-slate-200/80 shadow-tiny hover:border-slate-300 transition-all flex items-center justify-between" id="kpi-billing">
          <div className="space-y-1">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider font-sans">GR FACTURADO</p>
            <h3 className="text-base font-display font-bold text-emerald-600 tracking-tight">
              ${kpis.totalMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </h3>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">
              ${kpis.totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })} USD <span className="text-[9px] text-slate-500 font-mono">(FX: ${exchangeRate})</span>
            </p>
          </div>
          <div className="p-2 bg-emerald-50 rounded-md text-emerald-600 border border-emerald-100/50">
            <DollarSign className="h-5 w-5" />
          </div>
        </div>

      </div>

      {/* Graphics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" id="dashboard-graphics-row">
        
        {/* Folio Process Volume Bar Chart */}
        <div className="bg-white p-4 rounded-lg border border-slate-200/80 shadow-tiny lg:col-span-2">
          <div className="mb-3">
            <h4 className="text-xs font-display font-bold uppercase tracking-wider text-slate-900">Estatus de Operaciones Logísticas</h4>
            <p className="text-[11px] text-slate-500">Número de folios por etapa del proceso en tiempo real</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageChartData} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 9, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 9, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', borderRadius: '6px', border: 'none', color: '#fff' }}
                  labelStyle={{ fontWeight: 'bold', fontSize: 10, color: '#f8fafc' }}
                  itemStyle={{ fontSize: 10, color: '#3b82f6' }}
                />
                <Bar dataKey="cantidad" name="Folios" fill="#bc1100" radius={[4, 4, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Client Pie Chart */}
        <div className="bg-white p-4 rounded-lg border border-slate-200/80 shadow-tiny">
          <div className="mb-3">
            <h4 className="text-xs font-display font-bold uppercase tracking-wider text-slate-900">Volumen por Cliente</h4>
            <p className="text-[11px] text-slate-500">Participación logística por razón social</p>
          </div>
          <div className="h-44 relative flex items-center justify-center">
            {clientChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={clientChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={65}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {clientChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-xs text-slate-400 font-medium">No hay datos de clientes</div>
            )}
            <div className="absolute flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xl font-display font-black text-slate-850 leading-none">{folios.length}</span>
              <span className="text-[8px] text-slate-450 font-bold uppercase tracking-wider font-mono">Total Folios</span>
            </div>
          </div>
          <div className="mt-2 space-y-1 px-1 max-h-24 overflow-y-auto">
            {clientChartData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between text-[11px] text-slate-600">
                <div className="flex items-center gap-1 truncate">
                  <span className="h-1.5 w-1.5 rounded-full inline-block shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                  <span className="truncate">{item.name}</span>
                </div>
                <span className="font-semibold text-slate-800 font-mono shrink-0">{item.value} f</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Durations Analysis Line Chart */}
      <div className="bg-white p-4 rounded-lg border border-slate-200/80 shadow-tiny" id="durations-analysis-section">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 gap-2">
          <div>
            <h4 className="text-xs font-display font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
              <span>Duración Promedio de Procesos Logísticos</span>
              <span className="text-[10px] bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded font-mono font-bold">
                Tiempos en Tiempo Real
              </span>
            </h4>
            <p className="text-[11px] text-slate-500">Duración promedio calculada por etapa de trazabilidad para completar el ciclo operativo</p>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-600 bg-slate-50 px-2 py-1 rounded-md border border-slate-200/60 w-fit">
            <Clock className="h-3.5 w-3.5 text-blue-500" />
            <span className="font-semibold">Métrica en tiempo real</span>
          </div>
        </div>

        {/* Line Chart with always-visible data labels */}
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={durationChartData} margin={{ top: 20, right: 20, left: -20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="etapa" tick={{ fill: '#475569', fontSize: 9, fontFamily: 'monospace', fontWeight: 600 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 9, fontFamily: 'monospace' }} axisLine={false} tickLine={false} label={{ value: 'Minutos', angle: -90, position: 'insideLeft', offset: 12, fill: '#94a3b8', fontSize: 9, fontFamily: 'monospace' }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white shadow-xl text-xs space-y-1">
                        <div className="font-bold flex items-center gap-1.5">
                          <span>{data.icon}</span>
                          <span>{data.fullLabel}</span>
                        </div>
                        <div className="text-emerald-400 font-mono font-black text-sm">
                          {data.fullFormatted}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          Muestras registradas: <strong className="text-white">{data.sampleCount} folios</strong>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: 10 }} />
              <Line 
                type="monotone" 
                dataKey="Minutos Promedio" 
                stroke="#bc1100" 
                strokeWidth={2.5} 
                activeDot={{ r: 7, fill: '#bc1100', stroke: '#fff', strokeWidth: 2 }} 
                dot={{ r: 4, fill: '#bc1100' }}
              >
                <LabelList
                  dataKey="displayLabel"
                  position="top"
                  offset={10}
                  style={{
                    fill: '#991b1b',
                    fontSize: '10px',
                    fontWeight: '800',
                    fontFamily: 'monospace'
                  }}
                />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Process Breakdown Time Badges */}
        <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          {durationChartData.map(item => (
            <div key={item.key} className="bg-slate-50 border border-slate-200/80 rounded-lg p-2 text-center hover:bg-red-50/50 hover:border-red-200 transition-all">
              <div className="text-xs mb-0.5">{item.icon}</div>
              <div className="text-[9.5px] font-bold text-slate-700 truncate font-mono uppercase" title={item.fullLabel}>
                {item.etapa}
              </div>
              <div className="text-xs font-black text-slate-900 font-mono mt-0.5">
                {item.minutes > 0 ? (
                  item.minutes >= 60 ? (
                    <span className="text-brand-red">{(item.minutes / 60).toFixed(1)}h</span>
                  ) : (
                    <span>{item.minutes}m</span>
                  )
                ) : (
                  <span className="text-slate-400">0m</span>
                )}
              </div>
              <div className="text-[8.5px] text-slate-400 font-mono">
                {item.minutes > 0 ? `${item.minutes} min` : 'Sin datos'}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
